/**
 * ModelDownloadModal Component
 *
 * Modal dialog for downloading Ollama models.
 * Shows model info (size, VRAM requirements) before download
 * and displays real-time progress during download.
 */

import { useTranslation } from '../../hooks/useTranslation';
import { Download, HardDrive, Cpu, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import type { OllamaModel, DownloadProgress } from '../../hooks/useOllamaModels';

interface ModelDownloadModalProps {
  /** Model information */
  model: OllamaModel;
  /** Current download progress (null if not downloading) */
  progress: DownloadProgress | null;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when user confirms download */
  onConfirm: () => void;
  /** Called when user cancels or closes modal */
  onCancel: () => void;
}

export function ModelDownloadModal({
  model,
  progress,
  isOpen,
  onConfirm,
  onCancel,
}: ModelDownloadModalProps) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const isDownloading = progress?.status === 'downloading' || progress?.status === 'finalizing';
  const isCompleted = progress?.status === 'completed' || progress?.status === 'already_installed';
  const isError = progress?.status === 'error';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-secondary-800 rounded-xl p-6 max-w-md w-full shadow-xl animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="heading-lg-themed">
            {isDownloading
              ? t('ollama.downloading', 'Downloading...')
              : isCompleted
                ? t('ollama.downloadComplete', 'Download Complete!')
                : t('ollama.downloadModel', 'Download Model')}
          </h3>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-full transition-colors"
            disabled={isDownloading}
          >
            <X className="w-5 h-5 text-secondary-500" />
          </button>
        </div>

        {/* Model Info */}
        <div className="space-y-3 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-secondary-600 dark:text-secondary-400 flex items-center gap-2">
              <Download className="w-4 h-4" />
              {t('ollama.modelName', 'Model')}
            </span>
            <span className="font-medium text-secondary-900 dark:text-secondary-100">
              {model.id}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-secondary-600 dark:text-secondary-400 flex items-center gap-2">
              <HardDrive className="w-4 h-4" />
              {t('ollama.downloadSize', 'Download Size')}
            </span>
            <span className="font-medium text-secondary-900 dark:text-secondary-100">
              {model.size_gb} GB
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-secondary-600 dark:text-secondary-400 flex items-center gap-2">
              <Cpu className="w-4 h-4" />
              {t('ollama.vramRequired', 'VRAM Required')}
            </span>
            <span className="font-medium text-secondary-900 dark:text-secondary-100">
              {model.vram_gb} GB
            </span>
          </div>
          <p className="text-sm text-secondary-500 dark:text-secondary-400 pt-2 border-t border-secondary-200 dark:border-secondary-700">
            {model.description}
          </p>
        </div>

        {/* Progress Bar (when downloading) */}
        {isDownloading && progress && (
          <div className="mb-6">
            <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-3 mb-2 overflow-hidden">
              <div
                className="bg-blue-500 h-3 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress.progress}%` }}
              />
            </div>
            <div className="flex justify-between text-sm text-secondary-600 dark:text-secondary-400">
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {progress.progress}%
              </span>
              <div className="flex gap-4">
                {progress.speed && <span>{progress.speed}</span>}
                {progress.eta && <span>ETA: {progress.eta}</span>}
              </div>
            </div>
            {progress.message && (
              <p className="text-xs text-secondary-500 mt-2 truncate">{progress.message}</p>
            )}
          </div>
        )}

        {/* Completed State */}
        {isCompleted && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg text-green-700 dark:text-green-400 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span>
              {progress?.status === 'already_installed'
                ? t('ollama.alreadyInstalled', 'Model is already installed!')
                : t('ollama.downloadCompleteMessage', 'Download complete! Model is now available.')}
            </span>
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 rounded-lg text-red-700 dark:text-red-400 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-medium">{t('ollama.downloadError', 'Download failed')}</span>
              {progress?.message && <p className="text-sm mt-1 opacity-80">{progress.message}</p>}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          {!isDownloading && !isCompleted && (
            <>
              <button
                onClick={onCancel}
                className="px-4 py-2 text-secondary-600 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors min-h-[44px]"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 rounded-lg transition-colors flex items-center gap-2 min-h-[44px]"
              >
                <Download className="w-4 h-4" />
                {t('ollama.download', 'Download')}
              </button>
            </>
          )}
          {(isCompleted || isError) && (
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-secondary-500 text-white hover:bg-secondary-600 rounded-lg transition-colors min-h-[44px]"
            >
              {t('common.close', 'Close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModelDownloadModal;
